import React, { useMemo, useRef, useState } from 'react';
import { Card, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import MultiSelectFilter from './MultiSelectFilter';
import {
  filterOptionsFor,
  pruneFilters,
  subLocationsFor,
} from '../utils/meterFilters';

/**
 * Per-instance id source, so every label points at its own control. React 16
 * has no `useId`, and a label with nothing to point at is one a screen reader
 * cannot announce alongside the field it names.
 */
let nextFilterId = 0;

/**
 * Normalises an option to a value and a label.
 *
 * Most of these filters are worded lists where the text is the value. A
 * sub-location is not: it is named to the reader and identified to the backend
 * by a number, and passing the name as the value is how that filter came to
 * compare a label against a numeric id and match nothing.
 *
 * @param {string|{value: *, label: string}} option - Option as supplied.
 * @returns {{value: *, label: string}} The pair.
 */
const asOption = (option) =>
  option !== null && typeof option === 'object' ? option : { value: option, label: option };

/**
 * Single-select dropdown filter.
 *
 * When another selection has left exactly one option, that option is shown as
 * the value and the control is disabled. A closed select renders only its
 * placeholder, so a list narrowed to one reads identically to one holding
 * twelve — the reader is told nothing by the very thing that changed. Naming
 * the remaining value makes the narrowing visible, and disabling it stops a
 * control that has no choice left in it from looking as though it has one.
 *
 * It only does that while something else is selected. A fleet of water meters
 * has one module whether or not anything is filtered, and collapsing on that
 * alone put "Water" in the box the moment the bar was reset — which reads as a
 * reset that did not work.
 *
 * @param {string} label - Field label.
 * @param {string} placeholder - Label for the "no selection" option.
 * @param {*} value - Currently selected value.
 * @param {Array<string|{value: *, label: string}>} options - Selectable options.
 * @param {boolean} narrowed - Whether another filter is currently applied.
 * @param {Function} onChange - Called with the new value, or undefined when cleared.
 */
const SelectFilter = ({ label, placeholder, value, options, narrowed, onChange }) => {
  const id = useRef(`im-filter-${(nextFilterId += 1)}`).current;
  const settled = narrowed && options.length === 1 ? asOption(options[0]) : null;

  return (
    <Form.Group>
      <Form.Label htmlFor={id}>{label}</Form.Label>
      <Form.Select
        id={id}
        value={value ?? settled?.value ?? ''}
        disabled={Boolean(settled)}
        onChange={(event) => onChange(event.target.value || undefined)}
      >
        {!settled && <option value="">{placeholder}</option>}
        {options.map(asOption).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
};

/**
 * Filter controls for the meters list.
 *
 * Laid out as a single grid so the six filters and Reset share one row-flow —
 * Reset previously occupied a row of its own above them.
 * Emits partial filter changes; the caller owns how they are persisted.
 */
const FilterBar = ({
  meters,
  filters,
  onFiltersChange,
  onClearFilters,
  currentFinancialYear,
  sources,
}) => {
  /**
   * Every dropdown offers only what is still reachable. Each is computed from
   * the other selections rather than from its own, so a control that takes
   * several values can always be added to.
   */
  const filterOptions = useMemo(
    () => filterOptionsFor(meters, filters, currentFinancialYear),
    [meters, filters, currentFinancialYear]
  );

  /** Filters the last change stranded, named so the bar can say what it did. */
  const [cleared, setCleared] = useState([]);

  /**
   * Whether anything is filtering at all. A single remaining option only means
   * something once a selection has caused it — otherwise it is just the shape
   * of the fleet.
   */
  const narrowed = useMemo(
    () =>
      Object.values(filters || {}).some((value) =>
        Array.isArray(value) ? value.length > 0 : value !== undefined && value !== ''
      ),
    [filters]
  );

  /**
   * `label` carries the meter ID so the dropdown is searchable by it;
   * `shortLabel` is what the selected chip shows, keeping the control one line.
   */
  const meterIdOptions = useMemo(
    () =>
      meters
        .filter((meter) => filterOptions.meterIds.includes(meter.meterId))
        .map((meter) => ({
          value: meter.meterId,
          label: `${meter.meterName} (${meter.meterId})`,
          shortLabel: meter.meterName,
        })),
    [meters, filterOptions]
  );

  /**
   * Narrowed to the locations the named meters sit at — but only once there
   * are meters to narrow by. While the list is loading, or when its request
   * has failed, there are none, and filtering against that would leave the
   * control with nothing to offer rather than with everything.
   */
  const locationOptions = useMemo(
    () =>
      meters.length
        ? (sources || []).filter((source) => filterOptions.sourceIds.includes(source.value))
        : sources || [],
    [meters, sources, filterOptions]
  );

  /**
   * Sub-locations belong to a location, so the list follows whichever
   * locations are selected — and Location takes several, so this reads the
   * whole set. Derived here rather than handed down, so it cannot go stale
   * between a location changing and the options being recomputed.
   */
  const subLocationOptions = useMemo(
    () => subLocationsFor(sources, filters.sourceIds),
    [sources, filters.sourceIds]
  );

  /**
   * Applies the reader's change, and drops a sub-location the new location
   * selection has stranded.
   *
   * Removing one location chip can leave a sub-location belonging to it still
   * filtering the list, and changing a category strands the sub-category
   * beneath it. Either way the control stops offering that value and nothing
   * on screen says why the list is short, so the stranded filter is dropped
   * and named.
   *
   * This runs here rather than in an effect watching the filters: it is a
   * consequence of this action, and an effect would also fire on a change
   * arriving from the URL, clearing selections the reader had just linked to.
   *
   * @param {string} key - Filter being changed.
   * @param {*} value - Its new value.
   */
  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value === '' ? undefined : value };

    if (
      key === 'sourceIds' &&
      filters.subLocationId !== undefined &&
      !subLocationsFor(sources, next.sourceIds).some(
        (subLocation) => subLocation.value === filters.subLocationId
      )
    ) {
      next.subLocationId = undefined;
    }

    const { filters: pruned, dropped } = pruneFilters(next, meters, currentFinancialYear);

    setCleared(dropped);
    onFiltersChange(pruned);
  };

  const handleClear = () => {
    setCleared([]);
    onClearFilters();
  };

  return (
    <Card className="im-filter-card">
      <Card.Body>
        <div className="im-filter-field--wide">
          <MultiSelectFilter
            label="Meter ID"
            options={meterIdOptions}
            values={filters.meterIds}
            onChange={(values) => handleFilterChange('meterIds', values)}
            placeholder="Select Meter IDs..."
          />
        </div>

        <SelectFilter
          label="Module"
          placeholder="All Modules"
          value={filters.module}
          options={filterOptions.modules}
          narrowed={narrowed}
          onChange={(value) => handleFilterChange('module', value)}
        />

        <SelectFilter
          label="Category"
          placeholder="All Categories"
          value={filters.category}
          options={filterOptions.categories}
          narrowed={narrowed}
          onChange={(value) => handleFilterChange('category', value)}
        />

        <SelectFilter
          label="Sub Category"
          placeholder="All Sub Categories"
          value={filters.subCategory}
          options={filterOptions.subCategories}
          narrowed={narrowed}
          onChange={(value) => handleFilterChange('subCategory', value)}
        />

        <SelectFilter
          label="KPI"
          placeholder="All KPIs"
          value={filters.kpi}
          options={filterOptions.kpis}
          narrowed={narrowed}
          onChange={(value) => handleFilterChange('kpi', value)}
        />

        {sources && (
          <MultiSelectFilter
            label="Location"
            options={locationOptions}
            values={filters.sourceIds}
            onChange={(values) => handleFilterChange('sourceIds', values)}
            placeholder="All Locations"
          />
        )}

        {subLocationOptions.length > 0 && (
          <SelectFilter
            label="Sub Location"
            placeholder="All Sub Locations"
            value={filters.subLocationId}
            options={subLocationOptions}
            onChange={(value) =>
              handleFilterChange('subLocationId', value ? Number(value) : undefined)
            }
          />
        )}

        {cleared.length > 0 && (
          <p className="im-filter-note" role="status">
            {cleared.join(' and ')} {cleared.length > 1 ? 'were' : 'was'} cleared —
            no longer available with this selection.
          </p>
        )}

        <div className="im-filter-reset">
          <Button variant="link" className="btn" onClick={handleClear}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Reset Filters
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default FilterBar;
